import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SettingCategory, UpdateGlobalSettingDto } from '@repo/common-types';
import {
  getGlobalSettingsByCategoryRest,
  getTournamentSettingsRest,
  updateTournamentSettingRest,
  updateGlobalSettingRest,
} from './settings-service';
import { UpdateTournamentSettingDto } from '@repo/common-types/src/dto/table/settings.dto';

// Query Keys
export const settingsKeys = {
  globalByCategory: (category: SettingCategory) => ['global-settings', 'category', category] as const,
  tournamentSettings: (tournamentId: string) => ['tournament-settings', tournamentId] as const,
};

// Global Settings by Category Hook
export const useGlobalSettingsByCategory = (category: SettingCategory, enabled = true) => {
  return useQuery({
    queryKey: settingsKeys.globalByCategory(category),
    queryFn: () => {
      return getGlobalSettingsByCategoryRest(category);
    },
    enabled,
  });
};

// Tournament Settings Hook (returns merged settings)
export const useTournamentSettings = (tournamentId: string, enabled = true) => {
  return useQuery({
    queryKey: settingsKeys.tournamentSettings(tournamentId),
    queryFn: () => getTournamentSettingsRest(tournamentId),
    enabled: enabled && !!tournamentId,
  });
};

// Update Tournament Setting Mutation
export const useUpdateTournamentSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tournamentId,
      key,
      data,
    }: {
      tournamentId: string;
      key: string;
      data: UpdateTournamentSettingDto;
    }) => updateTournamentSettingRest(tournamentId, key, data),
    onSuccess: (result, variables) => {
      // Invalidate the tournament settings cache to refetch merged data
      queryClient.invalidateQueries({
        queryKey: settingsKeys.tournamentSettings(variables.tournamentId),
      });

      toast.success('Setting updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating tournament setting:', error);
      toast.error('Failed to update setting. Please try again.');
    },
  });
};

// Update Global Setting Mutation
export const useUpdateGlobalSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateGlobalSettingDto }) => updateGlobalSettingRest(key, data),
    onSuccess: (result, variables) => {
      // Invalidate all global settings caches since we don't know which category was updated
      queryClient.invalidateQueries({
        queryKey: ['global-settings'],
      });

      toast.success('Setting updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating global setting:', error);
      toast.error('Failed to update setting. Please try again.');
    },
  });
};
